// DEX Aggregator - Routes trades through Jupiter, Raydium, Orca for best prices
// This makes us superior to pump.fun by supporting ALL tokens

import { dataPipelineClient, DEXPriceResponse, TokenMetricsResponse, MarketDataResponse, WebSocketMessage } from './data-pipeline-client';

export interface DexQuote {
  dex: 'jupiter' | 'raydium' | 'orca' | 'moonrun';
  inputMint: string;
  outputMint: string;
  inputAmount: number;
  outputAmount: number;
  priceImpact: number;
  fee: number;
  route: string[];
  estimatedGas: number;
}

export interface TokenMetadata {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoURI?: string;
  tags?: string[];
  extensions?: {
    website?: string;
    twitter?: string;
    discord?: string;
  };
}

export interface ExternalToken {
  mint: string;
  symbol: string;
  name: string;
  decimals: number;
  supply: number;
  price: number;
  priceChange24h: number;
  volume24h: number;
  marketCap: number;
  liquidity: number;
  holders: number;
  logoURI?: string;
  isVerified: boolean;
  isCommunityLocked: boolean;
  lockVotes: number;
  unlockVotes: number;
}


// SOL mint address
export const SOL_MINT = 'So11111111111111111111111111111111111111112';
export const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

// Supabase URL for edge functions
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Jupiter API endpoints
const JUPITER_API = 'https://quote-api.jup.ag/v6';
const JUPITER_PRICE_API = 'https://price.jup.ag/v6';
const JUPITER_TOKEN_LIST = 'https://token.jup.ag/all';

// Get quote from Jupiter (best DEX aggregator on Solana)
export async function getJupiterQuote(
  inputMint: string,
  outputMint: string,
  amount: number,
  slippageBps: number = 50
): Promise<DexQuote | null> {
  try {
    const response = await fetch(
      `${JUPITER_API}/quote?inputMint=${inputMint}&outputMint=${outputMint}&amount=${amount}&slippageBps=${slippageBps}`
    );
    
    if (!response.ok) return null;
    
    const data = await response.json();
    
    return {
      dex: 'jupiter',
      inputMint,
      outputMint,
      inputAmount: amount,
      outputAmount: parseInt(data.outAmount),
      priceImpact: parseFloat(data.priceImpactPct) * 100,
      fee: 0, // Jupiter has no platform fee
      route: data.routePlan?.map((r: any) => r.swapInfo?.label) || [],
      estimatedGas: 5000, // ~5000 lamports
    };
  } catch (error) {
    console.error('Jupiter quote error:', error);
    return null;
  }
}

// Get token price from Jupiter
export async function getTokenPrice(mint: string): Promise<number | null> {
  try {
    const response = await fetch(`${JUPITER_PRICE_API}/price?ids=${mint}`);
    if (!response.ok) return null;
    
    const data = await response.json();
    return data.data?.[mint]?.price || null;
  } catch (error) {
    console.error('Price fetch error:', error);
    return null;
  }
}

// Get multiple token prices
export async function getTokenPrices(mints: string[]): Promise<Record<string, number>> {
  try {
    const response = await fetch(`${JUPITER_PRICE_API}/price?ids=${mints.join(',')}`);
    if (!response.ok) return {};
    
    const data = await response.json();
    const prices: Record<string, number> = {};
    
    for (const mint of mints) {
      if (data.data?.[mint]?.price) {
        prices[mint] = data.data[mint].price;
      }
    }
    
    return prices;
  } catch (error) {
    console.error('Prices fetch error:', error);
    return {};
  }
}

// Search tokens from Jupiter token list
export async function searchTokens(query: string): Promise<TokenMetadata[]> {
  try {
    const response = await fetch(JUPITER_TOKEN_LIST);
    if (!response.ok) return [];

    const tokens: TokenMetadata[] = await response.json();
    const lowerQuery = query.toLowerCase();

    return tokens
      .filter(t =>
        t.symbol.toLowerCase().includes(lowerQuery) ||
        t.name.toLowerCase().includes(lowerQuery) ||
        t.address.toLowerCase() === lowerQuery
      )
      .slice(0, 50);
  } catch (error) {
    console.error('Token search error:', error);
    return [];
  }
}


// Backend API functions delegating to dataPipelineClient
export async function getBackendDexPrices(): Promise<DEXPriceResponse[]> {
  return dataPipelineClient.getDEXPrices();
}

export async function getBackendTokenMetrics(address: string): Promise<TokenMetricsResponse | null> {
  try {
    return await dataPipelineClient.getTokenMetrics(address);
  } catch (error) {
    console.error('Backend token metrics error:', error);
    return null;
  }
}

export async function getBackendMarketData(): Promise<MarketDataResponse | null> {
  try {
    return await dataPipelineClient.getMarketData();
  } catch (error) {
    console.error('Backend market data error:', error);
    return null;
  }
}


// Get best quote across all DEXs
export async function getBestQuote(
  inputMint: string,
  outputMint: string,
  amount: number,
  slippageBps: number = 50
): Promise<DexQuote | null> {
  try {
    // Get Jupiter quote
    const jupiterQuote = await getJupiterQuote(inputMint, outputMint, amount, slippageBps);

    // Try to get backend DEX prices for comparison
    const backendPrices = await getBackendDexPrices();
    const backendPrice = backendPrices.find(p => p.tokenAddress === outputMint);

    if (!jupiterQuote) {
      // If no Jupiter quote, return null
      return null;
    }

    // If we have backend price data, we can enhance the quote
    // For now, we'll just return the Jupiter quote as it's already aggregated
    // But we could add logic here to compare and choose the best
    return jupiterQuote;
  } catch (error) {
    console.error('Best quote error:', error);
    // Fallback to Jupiter only
    return getJupiterQuote(inputMint, outputMint, amount, slippageBps);
  }
}

// Format swap transaction for signing
export async function getSwapTransaction(
  quote: any,
  userPublicKey: string
): Promise<string | null> {
  try {
    const response = await fetch(`${JUPITER_API}/swap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        quoteResponse: quote,
        userPublicKey,
        wrapAndUnwrapSol: true,
        computeUnitPriceMicroLamports: 'auto',
      }),
    });
    
    if (!response.ok) return null;
    
    const data = await response.json();
    return data.swapTransaction;
  } catch (error) {
    console.error('Swap transaction error:', error);
    return null;
  }
}

// Calculate price from reserves (for bonding curve tokens)
export function calculatePriceFromReserves(
  solReserve: number,
  tokenReserve: number
): number {
  return solReserve / tokenReserve;
}

// Estimate market cap
export function estimateMarketCap(
  price: number,
  totalSupply: number,
  solPriceUSD: number = 200
): number {
  return price * totalSupply * solPriceUSD;
}

// Get trending tokens - use edge function to avoid CORS
export async function getTrendingTokens(): Promise<ExternalToken[]> {
  try {
    // Try edge function first (avoids CORS)
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/token-search?type=trending`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      return data.tokens || [];
    }
    
    // Fallback to direct API call
    return getFallbackTrendingTokens();
  } catch (error) {
    console.error('Trending tokens error:', error);
    return getFallbackTrendingTokens();
  }
}

// Fallback trending using popular Solana token searches
async function getFallbackTrendingTokens(): Promise<ExternalToken[]> {
  try {
    // Search for trending pairs on Solana
    const response = await fetch('https://api.dexscreener.com/latest/dex/search?q=solana');
    
    if (!response.ok) return [];
    
    const data = await response.json();
    
    // Filter for Solana pairs with good volume
    const solanaPairs = data.pairs
      ?.filter((pair: any) => pair.chainId === 'solana' && pair.volume?.h24 > 10000)
      .sort((a: any, b: any) => (b.volume?.h24 || 0) - (a.volume?.h24 || 0))
      .slice(0, 12);
    
    if (!solanaPairs?.length) return [];
    
    return solanaPairs.map((pair: any) => ({
      mint: pair.baseToken?.address || '',
      symbol: pair.baseToken?.symbol || '',
      name: pair.baseToken?.name || '',
      decimals: 9,
      supply: 0,
      price: parseFloat(pair.priceUsd) || 0,
      priceChange24h: parseFloat(pair.priceChange?.h24) || 0,
      volume24h: parseFloat(pair.volume?.h24) || 0,
      marketCap: parseFloat(pair.marketCap) || parseFloat(pair.fdv) || 0,
      liquidity: parseFloat(pair.liquidity?.usd) || 0,
      holders: 0,
      logoURI: pair.info?.imageUrl,
      isVerified: !!pair.info?.socials?.length,
      isCommunityLocked: false,
      lockVotes: 0,
      unlockVotes: 0,
    }));
  } catch (error) {
    console.error('Fallback trending error:', error);
    return [];
  }
}

// Get token info from DexScreener
export async function getTokenFromDexScreener(mint: string): Promise<ExternalToken | null> {
  try {
    const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mint}`);
    if (!response.ok) return null;
    
    const data = await response.json();
    const pair = data.pairs?.[0];
    
    if (!pair) return null;
    
    return {
      mint,
      symbol: pair.baseToken?.symbol || '',
      name: pair.baseToken?.name || '',
      decimals: 9,
      supply: 0,
      price: parseFloat(pair.priceUsd) || 0,
      priceChange24h: parseFloat(pair.priceChange?.h24) || 0,
      volume24h: parseFloat(pair.volume?.h24) || 0,
      marketCap: parseFloat(pair.marketCap) || parseFloat(pair.fdv) || 0,
      liquidity: parseFloat(pair.liquidity?.usd) || 0,
      holders: 0,
      logoURI: pair.info?.imageUrl,
      isVerified: !!pair.info?.socials?.length,
      isCommunityLocked: false,
      lockVotes: 0,
      unlockVotes: 0,
    };
  } catch (error) {
    console.error('DexScreener fetch error:', error);
    return null;
  }
}

// Search tokens - use edge function to avoid CORS
export async function searchTokensDexScreener(query: string): Promise<ExternalToken[]> {
  try {
    // Try edge function first (avoids CORS)
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/token-search?query=${encodeURIComponent(query)}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.ok) {
      const data = await response.json();
      // Map to full ExternalToken format
      return (data.tokens || []).map((t: any) => ({
        mint: t.mint,
        symbol: t.symbol,
        name: t.name,
        decimals: t.decimals || 9,
        supply: 0,
        price: t.price || 0,
        priceChange24h: t.priceChange24h || 0,
        volume24h: t.volume24h || 0,
        marketCap: t.marketCap || 0,
        liquidity: t.liquidity || 0,
        holders: 0,
        logoURI: t.logoURI,
        isVerified: t.isVerified || false,
        isCommunityLocked: false,
        lockVotes: 0,
        unlockVotes: 0,
      }));
    }

    return [];
  } catch (error) {
    console.error('Token search error:', error);
    return [];
  }
}

// WebSocket subscription for real-time price updates
export function subscribeToPriceUpdates(subscriberId: string, callback: (message: WebSocketMessage) => void): void {
  dataPipelineClient.subscribeToPriceUpdates(subscriberId, callback);
}

export function unsubscribeFromPriceUpdates(subscriberId: string): void {
  dataPipelineClient.unsubscribeFromPriceUpdates(subscriberId);
}
